import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  FileCheck,
  Send,
  Loader2,
  BookOpen,
  User,
  CheckSquare,
  Square,
  Sparkles,
  Info,
  Phone,
  MessageCircle,
} from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';

export interface PublisherVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (verificationNote: string) => void;
  book: Book;
  isPending?: boolean;
}

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  category: string;
}

const VERIFICATION_CHECKLIST: ChecklistItem[] = [
  {
    id: 'metadata_title_author',
    title: 'Kelayakan Judul & Kepengarangan',
    description: 'Judul buku jelas, orisinal, serta nama dan identitas kepengarangan/penulis terverifikasi sah.',
    category: 'Identitas',
  },
  {
    id: 'category_classification',
    title: 'Kesesuaian Kategori & Klasifikasi',
    description: 'Kategori, genre, atau bidang ilmu naskah telah sesuai dengan format pengkatalogan resmi UIBN.',
    category: 'Katalog',
  },
  {
    id: 'synopsis_clarity',
    title: 'Kelengkapan Sinopsis & Abstrak',
    description: 'Sinopsis/deskripsi buku memuat gambaran lengkap alur atau substansi naskah tanpa informasi palsu.',
    category: 'Konten',
  },
  {
    id: 'toc_structure',
    title: 'Sistematika Daftar Isi & Bab Buku',
    description: 'Struktur daftar isi tersusun rapi, runut, dan mencerminkan susunan naskah yang siap diterbitkan.',
    category: 'Struktur',
  },
  {
    id: 'format_pages_spec',
    title: 'Format Naskah & Jumlah Halaman',
    description: 'Jumlah halaman, estimasi tahun terbit, dan kelengkapan teknis naskah memenuhi standar minimum penerbitan.',
    category: 'Spesifikasi',
  },
  {
    id: 'copyright_ethics',
    title: 'Orisinalitas & Kepatuhan Hak Cipta',
    description: 'Naskah bebas dari plagiarisme, pelanggaran hak cipta pihak ketiga, ujaran kebencian, atau unsur terlarang.',
    category: 'Legalitas',
  },
  {
    id: 'publisher_commitment',
    title: 'Persetujuan & Rekomendasi Penerbit',
    description: 'Lembaga penerbit menyatakan bertanggung jawab dan menyetujui penerusan berkas ke Admin Pusat UIBN.',
    category: 'Komitmen',
  },
];

export const PublisherVerificationModal: React.FC<PublisherVerificationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  book,
  isPending = false,
}) => {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [note, setNote] = useState('');

  // Reset checklist when modal opens
  useEffect(() => {
    if (isOpen) {
      setCheckedItems({});
      setNote('');
    }
  }, [isOpen, book?.id]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, isPending]);

  if (!isOpen || !book) return null;

  const totalItems = VERIFICATION_CHECKLIST.length;
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const isAllChecked = totalItems > 0 && checkedCount === totalItems;
  const progressPercent = Math.round((checkedCount / totalItems) * 100);

  const handleToggleItem = (id: string) => {
    if (isPending) return;
    setCheckedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleToggleAll = () => {
    if (isPending) return;
    if (isAllChecked) {
      setCheckedItems({});
    } else {
      const all: Record<string, boolean> = {};
      VERIFICATION_CHECKLIST.forEach((item) => {
        all[item.id] = true;
      });
      setCheckedItems(all);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAllChecked || isPending) return;

    const defaultNote = 'Naskah telah diverifikasi dan memenuhi seluruh checklist kelayakan kurasi penerbit resmi.';
    const finalNote = note.trim() ? `${note.trim()} (Telah lolos verifikasi 7/7 checklist penerbit)` : defaultNote;
    onConfirm(finalNote);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-xs">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="publisher-verify-title"
        >
          {/* Header */}
          <div className="px-5 py-4 sm:px-6 sm:py-4.5 bg-gradient-to-r from-indigo-900 via-indigo-800 to-slate-900 text-white flex items-center justify-between shrink-0 border-b border-indigo-700/50">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 text-emerald-400 shrink-0 shadow-inner">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 id="publisher-verify-title" className="text-base sm:text-lg font-bold text-white leading-tight">
                    Verifikasi Kelayakan Naskah
                  </h3>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 font-bold uppercase tracking-wider">
                    Kurasi Penerbit
                  </span>
                </div>
                <p className="text-xs text-indigo-200/90 truncate mt-0.5">
                  Lengkapi seluruh poin checklist kelayakan sebelum meneruskan naskah ke Admin UIBN.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              aria-label="Tutup modal verifikasi"
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition cursor-pointer shrink-0 disabled:opacity-50"
            >
              <X className="w-4.5 h-4.5" />
            </button>
          </div>

          {/* Body Content */}
          <div className="p-5 sm:p-6 overflow-y-auto space-y-5 flex-1 text-slate-700">
            {/* Book Info Summary Card */}
            <div className="p-3.5 sm:p-4 rounded-xl bg-slate-50 border border-slate-200/90 space-y-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-800 text-[10px] font-bold">
                      {book.category}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      Tahun {book.publicationYear}
                    </span>
                    {book.pages && (
                      <span className="text-[11px] text-slate-500">
                        • {book.pages} Hal
                      </span>
                    )}
                  </div>
                  <h4 className="font-bold text-slate-900 text-sm sm:text-base leading-snug">
                    {book.title}
                  </h4>
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-600 flex-wrap">
                    <span className="flex items-center gap-1 font-medium text-slate-700">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      Penulis: {book.authorName}
                    </span>
                    <span>•</span>
                    <span>Penerbit: {book.publisherName}</span>
                  </div>
                </div>

                {book.authorWhatsapp && (
                  <a
                    href={`https://wa.me/${book.authorWhatsapp.replace(/[^0-9]/g, '').replace(/^0/, '62')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shrink-0 shadow-2xs transition"
                    title="Hubungi Penulis via WhatsApp"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">WhatsApp</span>
                  </a>
                )}
              </div>
            </div>

            {/* Checklist Header & Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h5 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                    <FileCheck className="w-4 h-4 text-indigo-600" />
                    <span>Daftar Periksa Persyaratan ({checkedCount}/{totalItems})</span>
                  </h5>
                  <p className="text-[11px] text-slate-500">
                    Seluruh {totalItems} poin wajib diverifikasi dan dicentang oleh Penerbit.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleToggleAll}
                  disabled={isPending}
                  className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 transition cursor-pointer flex items-center gap-1.5"
                >
                  {isAllChecked ? (
                    <>
                      <Square className="w-3.5 h-3.5" />
                      <span>Hapus Semua Ceklis</span>
                    </>
                  ) : (
                    <>
                      <CheckSquare className="w-3.5 h-3.5" />
                      <span>Tandai Semua Selesai</span>
                    </>
                  )}
                </button>
              </div>

              {/* Progress bar */}
              <div className="space-y-1">
                <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden border border-slate-200 p-0.5">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      isAllChecked
                        ? 'bg-emerald-500'
                        : checkedCount > 0
                        ? 'bg-indigo-600'
                        : 'bg-transparent'
                    }`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <div className="flex justify-between items-center text-[10px] font-medium text-slate-500">
                  <span>Progres: {progressPercent}%</span>
                  <span>{isAllChecked ? '✓ Syarat Lengkap' : `${totalItems - checkedCount} syarat tersisa`}</span>
                </div>
              </div>
            </div>

            {/* Checklist Items List */}
            <div className="space-y-2.5">
              {VERIFICATION_CHECKLIST.map((item, index) => {
                const isChecked = Boolean(checkedItems[item.id]);
                return (
                  <div
                    key={item.id}
                    onClick={() => handleToggleItem(item.id)}
                    className={`p-3 rounded-xl border transition-all cursor-pointer select-none flex items-start gap-3 ${
                      isChecked
                        ? 'bg-emerald-50/70 border-emerald-300 ring-1 ring-emerald-400/20 shadow-2xs'
                        : 'bg-white hover:bg-slate-50/80 border-slate-200 shadow-2xs'
                    }`}
                  >
                    <div className="pt-0.5 shrink-0">
                      <div
                        className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${
                          isChecked
                            ? 'bg-emerald-600 text-white'
                            : 'border-2 border-slate-300 bg-white text-transparent'
                        }`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 fill-current" />
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase">
                          {item.category}
                        </span>
                        <h6
                          className={`text-xs font-bold transition-colors ${
                            isChecked ? 'text-emerald-950' : 'text-slate-900'
                          }`}
                        >
                          {index + 1}. {item.title}
                        </h6>
                      </div>
                      <p className="text-[11px] text-slate-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Verification Note (Optional) */}
            <div className="space-y-1.5 pt-2 border-t border-slate-200">
              <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                <span>Catatan / Rekomendasi Penerbit (Opsional):</span>
                <span className="text-[10px] text-slate-400 font-normal">Diteruskan ke Admin UIBN</span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={isPending}
                rows={2}
                placeholder="Tambahkan catatan kelayakan, nomor registrasi internal penerbit, atau pesan untuk Admin Pusat UIBN..."
                className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white text-slate-800 placeholder:text-slate-400 resize-none"
              />
            </div>

            {/* Validation Notice Banner */}
            {!isAllChecked ? (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200/90 text-amber-900 text-xs flex items-center gap-2.5">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                <span className="leading-snug">
                  Tandai seluruh <strong>{totalItems} poin checklist</strong> di atas untuk membuka tombol pengiriman ke Admin UIBN.
                </span>
              </div>
            ) : (
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200/90 text-emerald-900 text-xs flex items-center gap-2.5 animate-in fade-in duration-150">
                <Sparkles className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="leading-snug font-medium">
                  Seluruh syarat kurasi penerbit telah terpenuhi. Naskah siap diajukan ke Admin Pusat UIBN.
                </span>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="px-5 py-3.5 sm:px-6 sm:py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200/60 rounded-xl transition cursor-pointer disabled:opacity-50"
            >
              Batal
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isAllChecked || isPending}
              className={`px-5 py-2.5 rounded-xl font-bold text-xs flex items-center gap-2 transition shadow-sm cursor-pointer ${
                isAllChecked && !isPending
                  ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white shadow-indigo-500/20 active:scale-[0.99]'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>Memproses Pengajuan...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Kirim Pengajuan ke Admin UIBN</span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
